import React, { useState, useEffect } from 'react';
import { FiDownload, FiX, FiSmartphone } from 'react-icons/fi';
import { isInstallAvailable, promptInstall, isStandalone, isIOS } from '../pwa';
import './InstallPWAPrompt.css';

const DISMISS_KEY = 'aapm_install_dismissed_at';
const DISMISS_DAYS = 7;

const isRecentlyDismissed = () => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const dismissedAt = parseInt(v, 10);
    const now = Date.now();
    return (now - dismissedAt) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
};

const InstallPWAPrompt = () => {
  const [show, setShow] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;

    // Android / Chromium: wait for beforeinstallprompt event
    const onAvailable = () => setShow(true);
    window.addEventListener('pwa-install-available', onAvailable);

    // iOS: show A2HS hint after small delay
    if (isIOS()) {
      const t = setTimeout(() => setShowIOSHint(true), 2000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('pwa-install-available', onAvailable);
      };
    }

    // If already available
    if (isInstallAvailable()) setShow(true);

    return () => window.removeEventListener('pwa-install-available', onAvailable);
  }, []);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
    setShowIOSHint(false);
  };

  const handleInstall = async () => {
    const res = await promptInstall();
    if (res.outcome !== 'unavailable') {
      setShow(false);
    }
  };

  if (show) {
    return (
      <div className="pwa-install-banner" data-testid="pwa-install-banner">
        <FiSmartphone size={22} className="pwa-install-icon" />
        <div className="pwa-install-text">
          <div className="pwa-install-title">Install AA PM</div>
          <div className="pwa-install-sub">Add to your home screen for faster access & notifications.</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleInstall} data-testid="pwa-install-button">
          <FiDownload size={14} /> Install
        </button>
        <button className="pwa-install-close" onClick={handleDismiss} aria-label="Dismiss" data-testid="pwa-install-dismiss">
          <FiX size={18} />
        </button>
      </div>
    );
  }

  if (showIOSHint) {
    return (
      <div className="pwa-install-banner pwa-install-banner-ios" data-testid="pwa-install-banner-ios">
        <FiSmartphone size={22} className="pwa-install-icon" />
        <div className="pwa-install-text">
          <div className="pwa-install-title">Add to Home Screen</div>
          <div className="pwa-install-sub">
            Tap <span className="ios-share-icon">⎋</span> Share, then <strong>“Add to Home Screen”</strong> to install.
          </div>
        </div>
        <button className="pwa-install-close" onClick={handleDismiss} aria-label="Dismiss" data-testid="pwa-install-dismiss-ios">
          <FiX size={18} />
        </button>
      </div>
    );
  }

  return null;
};

export default InstallPWAPrompt;
